import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState, FormDialog } from '@ui-kit';
import {
  useMyAttachments,
  useUploadAttachment,
  useDeleteAttachment,
  usePreviewAttachment,
  useDownloadAttachment,
} from '../hooks/useAttachment';
import { DropZone } from '../components/DropZone';
import { AttachmentList } from '../components/AttachmentList';
import { PreviewPanel } from '../components/PreviewPanel';
import { MAX_FILE_SIZE_BYTES } from '../schema/attachmentSchema';
import type { AttachmentApiError } from '../types/attachment';

// ─── Keyframes ────────────────────────────────────────────────────────────────

const KEYFRAME_CSS = `
@keyframes attach-spin { to { transform: rotate(360deg); } }
@keyframes attach-fadein { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
`;

// ─── Result Card — learning feedback (회색/노랑 — not a red alert) ─────────────

interface UploadResultCardProps {
  result: { success: true; data: { fileId: string; originalName: string; size: number; mimeType: string } } | { success: false; error: AttachmentApiError } | null;
  onDismiss: () => void;
}

function UploadResultCard({ result, onDismiss }: UploadResultCardProps) {
  const { t } = useTranslation();
  if (!result) return null;

  const isSuccess = result.success;
  const borderColor = isSuccess ? 'var(--color-success)' : 'var(--color-warning)';
  const badgeBg = isSuccess
    ? 'color-mix(in srgb, var(--color-success) 18%, transparent)'
    : 'color-mix(in srgb, var(--color-warning) 22%, transparent)';
  const badgeColor = isSuccess ? 'var(--color-success)' : 'var(--color-warning)';

  const error = !isSuccess ? (result as { success: false; error: AttachmentApiError }).error : null;

  // Learning hint based on error code
  // ATTACH-415-001: biz layer translated from C009 FILE-400-003/004 → HTTP 415
  // FILE-400-003 / FILE-400-004: fallback if raw C009 code reaches FE (shouldn't after fix)
  let hint = '';
  if (error) {
    if (
      error.code.includes('413') ||
      error.code.includes('SIZE') ||
      error.code === 'FILE-400-002'
    ) {
      hint = t('attachmentDemo.resultCard.learnHint413');
    } else if (
      error.code.includes('415') ||
      error.code.includes('MIME') ||
      error.code === 'ATTACH-415-001' ||
      error.code === 'FILE-400-003' ||
      error.code === 'FILE-400-004'
    ) {
      hint = t('attachmentDemo.resultCard.learnHint415');
    } else {
      hint = t('attachmentDemo.resultCard.learnHintDefault');
    }
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        background: isSuccess
          ? 'color-mix(in srgb, var(--color-success) 8%, var(--color-surface-muted))'
          : 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-3)',
        marginTop: 'var(--spacing-4)',
        animation: 'attach-fadein 0.25s ease',
      }}
    >
      {/* Card header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 'var(--spacing-2)',
          paddingBottom: 'var(--spacing-2)',
          borderBottom: '1px dashed var(--color-surface-border)',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          {isSuccess ? t('attachmentDemo.resultCard.uploadSuccessTitle') : t('attachmentDemo.resultCard.uploadErrorTitle')}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 700,
              padding: '2px var(--spacing-3)',
              borderRadius: 'var(--radius-full)',
              background: badgeBg,
              color: badgeColor,
              border: `1px solid ${badgeColor}`,
            }}
          >
            {isSuccess ? `✓ ${t('attachmentDemo.resultCard.uploadSuccessBadge')}` : `⚠ ${t('attachmentDemo.resultCard.uploadErrorBadge')}`}
          </span>
          {error && (
            <span
              style={{
                display: 'inline-flex',
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 700,
                padding: '2px var(--spacing-3)',
                borderRadius: 'var(--radius-full)',
                background: error.statusCode === 413 || error.statusCode === 415
                  ? 'var(--color-warning)'
                  : error.statusCode === 403
                  ? 'var(--color-surface-elevated)'
                  : 'var(--color-danger)',
                color: error.statusCode === 403 ? 'var(--color-text-secondary)' : 'var(--color-text-inverse)',
                border: error.statusCode === 403 ? '1px solid var(--color-text-muted)' : 'none',
              }}
            >
              {error.statusCode}
            </span>
          )}
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t('attachmentDemo.resultCard.dismissAriaLabel')}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'inherit',
              padding: '2px var(--spacing-2)',
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Data rows */}
      {isSuccess &&
        (() => {
          const d = (result as { success: true; data: { fileId: string; originalName: string; size: number; mimeType: string } }).data;
          return (
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: '130px 1fr',
                gap: 'var(--spacing-2) var(--spacing-3)',
                fontSize: 'var(--font-size-sm)',
                margin: 0,
              }}
            >
              {[
                { label: 'fileId', value: d.fileId },
                { label: 'originalName', value: d.originalName },
                { label: 'size', value: `${d.size} bytes` },
                { label: 'mimeType', value: d.mimeType },
              ].map(({ label, value }) => (
                <>
                  <dt
                    key={`dt-${label}`}
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      paddingTop: 2,
                    }}
                  >
                    {label}
                  </dt>
                  <dd
                    key={`dd-${label}`}
                    style={{
                      color: 'var(--color-text-primary)',
                      fontFamily: 'var(--font-family-mono)',
                      fontSize: 'var(--font-size-xs)',
                      wordBreak: 'break-all',
                      margin: 0,
                    }}
                  >
                    {value}
                  </dd>
                </>
              ))}
            </dl>
          );
        })()}

      {error && (
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: '130px 1fr',
            gap: 'var(--spacing-2) var(--spacing-3)',
            fontSize: 'var(--font-size-sm)',
            margin: 0,
          }}
        >
          {[
            { label: 'code', value: error.code, highlight: true },
            { label: 'message', value: error.message },
            ...(error.detail ? [{ label: 'detail', value: error.detail }] : []),
            ...(error.traceId ? [{ label: 'traceId', value: error.traceId }] : []),
          ].map(({ label, value, highlight }) => (
            <>
              <dt
                key={`dt-${label}`}
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  paddingTop: 2,
                }}
              >
                {label}
              </dt>
              <dd
                key={`dd-${label}`}
                style={{
                  color: highlight ? 'var(--color-brand-primary)' : 'var(--color-text-primary)',
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  wordBreak: 'break-all',
                  margin: 0,
                  fontWeight: highlight ? 700 : undefined,
                }}
              >
                {value}
              </dd>
            </>
          ))}
        </dl>
      )}

      {/* Learning hint */}
      {hint && (
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            lineHeight: 1.5,
            padding: 'var(--spacing-2) var(--spacing-3)',
            background: 'var(--color-surface-elevated)',
            borderRadius: 'var(--radius-sm)',
            border: '1px dashed var(--color-surface-border)',
          }}
        >
          <strong style={{ color: 'var(--color-brand-primary)', fontFamily: 'var(--font-family-mono)' }}>
            {t('attachmentDemo.resultCard.learnPoint')}{' '}
          </strong>
          {hint}
        </div>
      )}
    </div>
  );
}

// ─── Common Modules Panel ─────────────────────────────────────────────────────

function CommonModulesPanel() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const COMMON_MODULES = [
    {
      module: 'eraf-core C009',
      api: 'FileService.upload(MultipartFile, FileMeta)',
      role: t('attachmentDemo.common.modules.fileService'),
      snippet: 'fileService.upload(file, meta)',
    },
    {
      module: 'eraf-core C009',
      api: 'FileValidator.validate(file, policy)',
      role: t('attachmentDemo.common.modules.fileValidator'),
      snippet: 'fileValidator.validate(file, policy())',
    },
    {
      module: 'eraf-core C009',
      api: 'MimeDetector.detect(InputStream)',
      role: t('attachmentDemo.common.modules.mimeDetector'),
      snippet: 'mimeDetector.detect(stream)',
    },
    {
      module: 'eraf-core C001',
      api: 'ApiResponse.success(data)',
      role: t('attachmentDemo.common.modules.apiResponse'),
      snippet: 'return ApiResponse.success(resp)',
    },
    {
      module: 'eraf-core C001',
      api: 'GlobalExceptionHandler',
      role: t('attachmentDemo.common.modules.exceptionHandler'),
      snippet: t('attachmentDemo.common.modules.snippetAdvice'),
    },
    {
      module: 'eraf-core C006',
      api: 'RequestContextHolder.current().userId()',
      role: t('attachmentDemo.common.modules.requestContext'),
      snippet: 'RequestContextHolder.current().userId()',
    },
    {
      module: 'eraf-core C014',
      api: '@Audited(action=CREATE/DELETE/READ)',
      role: t('attachmentDemo.common.modules.audited'),
      snippet: '@Audited(action=CREATE, resourceType="attachment-demo")',
    },
    {
      module: 'eraf-security C028',
      api: 'JwtAuthenticationFilter',
      role: t('attachmentDemo.common.modules.jwtFilter'),
      snippet: t('attachmentDemo.common.modules.snippetFilter'),
    },
  ];

  return (
    <div
      style={{
        marginTop: 'var(--spacing-8)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="attachment-demo-common-panel"
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-elevated)',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-family-sans)',
        }}
      >
        <span>{t('attachmentDemo.common.title')}</span>
        <span aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div id="attachment-demo-common-panel" style={{ overflowX: 'auto' }}>
          <table
            aria-label={t('attachmentDemo.common.ariaLabel')}
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-xs)',
              background: 'var(--color-surface-background)',
            }}
          >
            <thead>
              <tr>
                {[
                  t('attachmentDemo.common.col.module'),
                  t('attachmentDemo.common.col.api'),
                  t('attachmentDemo.common.col.role'),
                  t('attachmentDemo.common.col.snippet'),
                ].map((h) => (
                  <th
                    key={h}
                    scope="col"
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      textAlign: 'left',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontWeight: 700,
                      background: 'var(--color-surface-muted)',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMMON_MODULES.map((m, idx) => (
                <tr key={idx}>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: idx < COMMON_MODULES.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {m.module}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: idx < COMMON_MODULES.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                    }}
                  >
                    <code
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        color: 'var(--color-brand-primary)',
                        fontSize: 'var(--font-size-xs)',
                        wordBreak: 'break-all',
                      }}
                    >
                      {m.api}
                    </code>
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: idx < COMMON_MODULES.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {m.role}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: idx < COMMON_MODULES.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                    }}
                  >
                    <code
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        background: 'var(--color-surface-elevated)',
                        padding: '2px var(--spacing-2)',
                        borderRadius: 'var(--radius-sm)',
                        display: 'inline-block',
                        wordBreak: 'break-all',
                      }}
                    >
                      {m.snippet}
                    </code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── AttachmentDemoPage ───────────────────────────────────────────────────────

type UploadResult =
  | { success: true; data: { fileId: string; originalName: string; size: number; mimeType: string } }
  | { success: false; error: AttachmentApiError }
  | null;

/**
 * /attachments — single-page demo for C009 FileService lifecycle
 * Layout: PageHeader → DropZone (top) → AttachmentList + PreviewPanel (split) → CommonModulesPanel
 */
export function AttachmentDemoPage() {
  const { t } = useTranslation();
  // State
  const [page] = useState(0);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ fileId: string; name: string } | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult>(null);
  const [currentUploadingName, setCurrentUploadingName] = useState<string | undefined>();

  // Queries & mutations
  const { data: listData, isLoading: listLoading, isError: listError, error: listErr, refetch } =
    useMyAttachments(page, 20);

  const uploadMutation = useUploadAttachment();
  const deleteMutation = useDeleteAttachment();
  const { data: previewMeta, isLoading: previewLoading, isError: previewError, error: previewErr } =
    usePreviewAttachment(selectedFileId);
  const { download } = useDownloadAttachment();

  const items = listData?.content ?? [];
  const isEmpty = !listLoading && !listError && items.length === 0;

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleFileDrop = useCallback(
    (file: File) => {
      setUploadResult(null);

      // R9-D-004: client-side 5MB validation — avoid nginx 413 raw HTML round-trip
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setUploadResult({
          success: false,
          error: {
            code: 'FILE-400-002',
            message: t('attachment.error.tooLarge',
              { defaultValue: `파일 크기가 ${(MAX_FILE_SIZE_BYTES / (1024 * 1024)).toFixed(0)}MB를 초과했습니다. (${(file.size / (1024 * 1024)).toFixed(1)}MB)` }),
            detail: `${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB) exceeds 5 MB limit`,
            statusCode: 413,
          },
        });
        return;
      }

      setCurrentUploadingName(file.name);
      uploadMutation.mutate(file, {
        onSuccess: (data) => {
          setUploadResult({
            success: true,
            data: {
              fileId: data.fileId,
              originalName: data.originalName,
              size: data.size,
              mimeType: data.mimeType,
            },
          });
          setCurrentUploadingName(undefined);
        },
        onError: (err) => {
          setUploadResult({ success: false, error: err });
          setCurrentUploadingName(undefined);
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uploadMutation, t]
  );

  const handlePreview = useCallback((fileId: string) => {
    setSelectedFileId((prev) => (prev === fileId ? null : fileId));
  }, []);

  const handleDownload = useCallback(
    (fileId: string, originalName: string) => {
      void download(fileId, originalName);
    },
    [download]
  );

  const handleDeleteRequest = useCallback((fileId: string, name: string) => {
    setDeleteTarget({ fileId, name });
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.fileId, {
      onSuccess: () => {
        if (selectedFileId === deleteTarget.fileId) setSelectedFileId(null);
        setDeleteTarget(null);
      },
      onError: () => {
        setDeleteTarget(null);
      },
    });
  }, [deleteTarget, deleteMutation, selectedFileId]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const selectedItem = items.find((i) => i.fileId === selectedFileId);

  // Preview panel restored — #620: BE GET /attachments/{id}/preview is live.
  const showPreviewPanel = true;

  // ─── Keyboard: Escape closes preview & dialogs ────────────────────────────

  return (
    <>
      <style>{KEYFRAME_CSS}</style>

      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: 'var(--spacing-6)',
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            if (deleteTarget) handleDeleteCancel();
            else if (selectedFileId) setSelectedFileId(null);
          }
        }}
      >
        {/* Page Header */}
        <PageHeader
          title={t('attachmentDemo.page.title')}
          breadcrumb={
            <span>
              {t('attachmentDemo.page.breadcrumb')} /{' '}
              <span style={{ color: 'var(--color-text-primary)' }}>{t('attachmentDemo.page.title')}</span>
            </span>
          }
          actions={
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                padding: '2px var(--spacing-3)',
                borderRadius: 'var(--radius-full)',
                background: 'color-mix(in srgb, var(--color-info) 18%, transparent)',
                color: 'var(--color-info)',
                border: '1px solid var(--color-info)',
              }}
            >
              <span aria-hidden="true">👤</span>
              ROLE_USER
            </span>
          }
        />

        {/* Description */}
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            marginTop: 'var(--spacing-3)',
            marginBottom: 'var(--spacing-6)',
          }}
        >
          {t('attachmentDemo.page.description')}
        </p>

        {/* DropZone — always visible */}
        <DropZone
          onFileDrop={handleFileDrop}
          isUploading={uploadMutation.isPending}
          uploadProgress={uploadMutation.uploadProgress}
          uploadingFileName={currentUploadingName}
          emphasize={isEmpty}
          disabled={false}
        />

        {/* Upload result card */}
        <UploadResultCard
          result={uploadResult}
          onDismiss={() => setUploadResult(null)}
        />

        {/* Error state — list load failure */}
        {listError && (
          <ErrorState
            message={
              (listErr as AttachmentApiError)?.message ?? t('attachmentDemo.error.listFailed')
            }
            onRetry={() => void refetch()}
          />
        )}

        {/* Main content: list + optional preview panel */}
        {!listError && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: showPreviewPanel && selectedFileId
                ? '1fr 320px'
                : '1fr',
              gap: 'var(--spacing-4)',
              alignItems: 'start',
              // Responsive: single column below 768px
            }}
          >
            {/* Left — attachment list */}
            <div>
              {/* Loading state */}
              {listLoading && (
                <AttachmentList
                  items={[]}
                  isLoading={true}
                  onDownload={handleDownload}
                  onPreview={handlePreview}
                  onDeleteRequest={handleDeleteRequest}
                  selectedFileId={selectedFileId}
                />
              )}

              {/* Empty state */}
              {isEmpty && (
                <>
                  <AttachmentList
                    items={[]}
                    isLoading={false}
                    onDownload={handleDownload}
                    onPreview={handlePreview}
                    onDeleteRequest={handleDeleteRequest}
                    selectedFileId={selectedFileId}
                  />
                  <div style={{ marginTop: 'var(--spacing-4)' }}>
                    <EmptyState
                      title={t('attachmentDemo.empty.title')}
                      description={`${t('attachmentDemo.empty.description')} ${t('attachmentDemo.empty.hint')}`}
                      action={
                        <button
                          type="button"
                          onClick={() => {
                            const input = document.querySelector<HTMLInputElement>(
                              '[data-testid="dropzone-input"]'
                            );
                            input?.click();
                          }}
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-4)',
                            background: 'var(--color-brand-primary)',
                            color: 'var(--color-text-inverse)',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            fontSize: 'var(--font-size-sm)',
                            fontFamily: 'var(--font-family-sans)',
                            fontWeight: 600,
                          }}
                        >
                          {t('attachmentDemo.empty.selectBtn')}
                        </button>
                      }
                    />
                  </div>
                </>
              )}

              {/* Default state — list with items */}
              {!listLoading && !isEmpty && (
                <AttachmentList
                  items={items}
                  isLoading={false}
                  onDownload={handleDownload}
                  onPreview={handlePreview}
                  onDeleteRequest={handleDeleteRequest}
                  selectedFileId={selectedFileId}
                />
              )}
            </div>

            {/* Right — preview panel: restored #620 */}
            {showPreviewPanel && selectedFileId && (
              <PreviewPanel
                fileId={selectedFileId}
                meta={previewMeta ?? null}
                isLoading={previewLoading}
                isError={previewError}
                errorMessage={(previewErr as AttachmentApiError | undefined)?.message}
                originalName={selectedItem?.originalName}
                onClose={() => setSelectedFileId(null)}
              />
            )}
          </div>
        )}

        {/* Delete confirm dialog */}
        <FormDialog
          open={deleteTarget !== null}
          title={t('attachmentDemo.dialog.deleteTitle')}
          submitLabel={deleteMutation.isPending ? t('attachmentDemo.dialog.deletingBtn') : t('attachmentDemo.dialog.confirmBtn')}
          cancelLabel={t('attachmentDemo.dialog.cancelBtn')}
          onClose={handleDeleteCancel}
          onSubmit={handleDeleteConfirm}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: 0 }}>
              {t('attachmentDemo.dialog.deleteMessage')}
            </p>
            {deleteTarget && (
              <div
                style={{
                  background: 'color-mix(in srgb, var(--color-info) 12%, var(--color-surface-elevated))',
                  border: '1px solid var(--color-info)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--spacing-3)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                {deleteTarget.name}
              </div>
            )}
            <div
              style={{
                background: 'color-mix(in srgb, var(--color-danger) 10%, var(--color-surface-elevated))',
                border: '1px solid var(--color-danger)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--spacing-3)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-primary)',
              }}
            >
              <strong style={{ color: 'var(--color-danger)' }}>⚠ {t('attachmentDemo.dialog.caution')}</strong>{' '}
              {t('attachmentDemo.dialog.deleteWarn')}
            </div>
          </div>
        </FormDialog>

        {/* Common modules panel */}
        <CommonModulesPanel />
      </div>

      {/* Responsive style */}
      <style>{`
        @media (max-width: 768px) {
          [data-page-grid] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
}
